import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';

import { useTypedParams } from '@sorare/routing';

import { isType } from '@sorare/core/src/gql';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';

import { Matches as DumbMatches } from '@sorare/gameplay/src/components/seo/Matches';
import { PlayerGameScoreDialog } from '@sorare/gameplay/src/components/stats/PlayerGameScoreDialog';
import { anyGameToGenericGame } from '@sorare/gameplay/src/lib/anyGame';
import { anyPlayerGameStatsToGenericGame } from '@sorare/gameplay/src/lib/anyPlayerGameStats';

import {
  NbaPlayerFutureFixturesQuery,
  NbaPlayerFutureFixturesQueryVariables,
  NbaPlayerPastFixturesQuery,
  NbaPlayerPastFixturesQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const NBA_PLAYER_PAST_FIXTURES_QUERY = gql`
  query NbaPlayerPastFixturesQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      anyGameStats(last: 30) {
        id
        ...anyPlayerGameStatsToGenericGame_anyPlayerGameStats
      }
    }
  }
  ${anyPlayerGameStatsToGenericGame.fragments.anyPlayerGameStats}
` as TypedDocumentNode<
  NbaPlayerPastFixturesQuery,
  NbaPlayerPastFixturesQueryVariables
>;

const NBA_PLAYER_FUTURE_FIXTURES_QUERY = gql`
  query NbaPlayerFutureFixturesQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      displayName
      activeClub {
        slug
        upcomingAnyGames(first: 30) {
          id
          ...anyGameToGenericGame_anyGameInterface
        }
      }
    }
  }
  ${anyGameToGenericGame.fragments.anyGameInterface}
` as TypedDocumentNode<
  NbaPlayerFutureFixturesQuery,
  NbaPlayerFutureFixturesQueryVariables
>;

const Games = ({ displayName }: { displayName: string }) => {
  const { slug } = useTypedParams<PageParams>();
  const [playerGameScoreId, setPlayerGameScoreId] = useState<string>('');

  const { data: dataFuture, loading: loadingFuture } = usePaginatedQuery(
    NBA_PLAYER_FUTURE_FIXTURES_QUERY,
    {
      variables: { slug },
      connection: 'GameConnection',
    }
  );

  const { data: dataPast, loading: loadingPast } = usePaginatedQuery(
    NBA_PLAYER_PAST_FIXTURES_QUERY,
    {
      variables: { slug },
      connection: 'GameConnection',
    }
  );
  const pastNbaPlayer = dataPast?.anyPlayer;
  if (!pastNbaPlayer || !isType(pastNbaPlayer, 'NBAPlayer')) return null;

  const pastPlayerGameStats = [...(pastNbaPlayer?.anyGameStats || [])]
    .filter(Boolean)
    .reverse();

  const futureNbaPlayer = dataFuture?.anyPlayer;
  if (!futureNbaPlayer || !isType(futureNbaPlayer, 'NBAPlayer')) return null;

  const futureGames =
    futureNbaPlayer?.activeClub?.upcomingAnyGames.filter(Boolean) || [];

  return (
    <>
      <DumbMatches
        displayName={displayName}
        loading={loadingPast || loadingFuture}
        pastGames={pastPlayerGameStats.map(playerGameStats =>
          anyPlayerGameStatsToGenericGame({
            playerGameStats,
            onClick: id => setPlayerGameScoreId(idFromObject(id)),
          })
        )}
        infiniteScrollLoaderPast={null}
        futureGames={futureGames.map(anyGame =>
          anyGameToGenericGame({ anyGame })
        )}
        infiniteScrollLoaderFuture={null}
      />
      {!!setPlayerGameScoreId && (
        <PlayerGameScoreDialog
          open={!!playerGameScoreId}
          showUpsell={false}
          playerGameScoreId={playerGameScoreId}
          onClose={() => setPlayerGameScoreId('')}
        />
      )}
    </>
  );
};

export default Games;
